<script lang="ts">
import { encodeQueryParams } from '@/utils/queryParams';
import {
  iButtonToggle,
  iButtonToggleType,
  iButtonToggleSize,
  iTextInput,
  iTextInputModes,
  iModal,
  openModal,
  closeModal,
  showToast,
} from '@inscribe/signal';
import axios from 'axios';
import confetti from 'canvas-confetti';

const SENTIMENT_TYPE = {
  POSITIVE: 'POSITIVE',
  NEGATIVE: 'NEGATIVE',
  NONE: null,
};

export default {
  name: 'UserFeedback',
  components: {
    iButtonToggle,
    iTextInput,
    iModal,
  },
  props: {
    resourceType: {
      type: String,
      required: true,
    },
    resourceId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      iButtonToggleType,
      iButtonToggleSize,
      iTextInputModes,
      feedbackSentiment: SENTIMENT_TYPE.NONE,
      feedbackText: '',
      isSubmitting: false,
      fetchAbortController: undefined,
      validationError: null,
      SENTIMENT_TYPE,
    };
  },
  computed: {
    modalId() {
      return `user-feedback-modal-${this.resourceId}`;
    },
  },
  mounted() {
    this.fetchFeedback();
  },
  methods: {
    throwConfetti() {
      confetti({
        zIndex: 1000000,
        angle: 270,
        spread: 120,
        startVelocity: 30,
        origin: {
          x: 0.8,
          y: 0,
        },
        disableForReducedMotion: true,
      });
    },
    async fetchFeedback() {
      // if there is a previous inflight request, cancel it
      if (this.fetchAbortController) {
        this.fetchAbortController.abort();
      }
      this.fetchAbortController = new AbortController();

      try {
        const res = await axios.get(
          `/api/v2/feedback?${encodeQueryParams({
            resource_type: this.resourceType,
            resource_id: this.resourceId,
          })}`,
          {
            signal: this.fetchAbortController.signal,
          },
        );
        if (res.data.data.sentiment !== SENTIMENT_TYPE.NONE) {
          const { sentiment } = res.data.data;
          this.feedbackSentiment = sentiment;
        }
      } catch (e) {
        if (e.message !== 'canceled') {
          showToast({
            text: 'Error fetching feedback',
            isError: true,
          });
        }
      }
    },
    async submitFeedback(isPositive: boolean, comments: string) {
      this.validationError = null;
      this.isSubmitting = true;
      try {
        await axios.put('/api/v2/feedback', {
          resource_type: this.resourceType,
          resource_id: this.resourceId,
          sentiment: isPositive
            ? SENTIMENT_TYPE.POSITIVE
            : SENTIMENT_TYPE.NEGATIVE,
          comments: comments,
          web_app_url: window.location.href,
        });
        this.throwConfetti();
        setTimeout(
          () =>
            showToast({
              text: "Thanks for your feedback! We'll use it to improve fraud accuracy.",
            }),
          500,
        );
      } catch {
        showToast({
          text: 'Error submitting feedback. Please try again.',
          isError: true,
        });
      } finally {
        this.isSubmitting = false;
        // refetch feedback in background to ensure UI is in sync with backend
        this.fetchFeedback();
      }
    },
    async deleteFeedback() {
      this.isSubmitting = true;
      try {
        await axios.delete(
          `/api/v2/feedback?${encodeQueryParams({
            resource_type: this.resourceType,
            resource_id: this.resourceId,
          })}`,
        );
      } catch {
        showToast({
          text: 'Error removing feedback. Please try again.',
          isError: true,
        });
      } finally {
        this.isSubmitting = false;
        // refetch feedback in background to ensure UI is in sync with backend
        this.fetchFeedback();
      }
    },
    onChangeFeedbackText(newText) {
      this.feedbackText = newText;
    },
    onCancelModal() {
      this.feedbackSentiment = SENTIMENT_TYPE.NONE;
      this.validationError = null;
    },
    onFeedbackClicked(sentiment: string, value: boolean) {
      if (value) {
        if (sentiment === SENTIMENT_TYPE.POSITIVE) {
          this.feedbackSentiment = sentiment;
          this.submitFeedback(true, '');
        } else {
          openModal(this.modalId);
        }
      } else {
        this.feedbackSentiment = SENTIMENT_TYPE.NONE;
        this.deleteFeedback();
      }
    },
    async onModalSubmitClicked() {
      if (!this.feedbackText) {
        this.validationError = 'This field is required';
        return;
      }
      await this.submitFeedback(false, this.feedbackText);
      closeModal(this.modalId);
      this.feedbackText = '';
      this.validationError = null;
      this.feedbackSentiment = SENTIMENT_TYPE.NEGATIVE;
    },
  },
};
</script>

<template>
  <div
    :class="{
      'functionality-disabled': isSubmitting,
    }"
    class="user-feedback d-flex flex-row align-items-center i-gap-1 i-px-2"
  >
    <i-button-toggle
      :size="iButtonToggleSize.SMALL"
      :type="iButtonToggleType.THUMBS_UP"
      tooltip-title="Positive"
      :value="feedbackSentiment === SENTIMENT_TYPE.POSITIVE"
      @value-changed="
        (value) => onFeedbackClicked(SENTIMENT_TYPE.POSITIVE, value)
      "
    />
    <i-button-toggle
      :size="iButtonToggleSize.SMALL"
      :type="iButtonToggleType.THUMBS_DOWN"
      tooltip-title="Negative"
      :value="feedbackSentiment === SENTIMENT_TYPE.NEGATIVE"
      @value-changed="
        (value) => onFeedbackClicked(SENTIMENT_TYPE.NEGATIVE, value)
      "
    />

    <i-modal
      :modal-id="modalId"
      data-testid="user-feedback-modal"
      title="Send Feedback"
      subtitle="Help us catch more fraud for you"
      confirm-button-text="Submit"
      :input-focus-on-open="true"
      :submit-on-enter="false"
      :close-on-submit="false"
      :confirm-button-loading="isSubmitting"
      @on-submit="onModalSubmitClicked"
      @on-cancel="onCancelModal"
      @on-close="onCancelModal"
    >
      <template #body-content>
        <div class="d-flex flex-column i-gap-4">
          <i-text-input
            ref="feedbackTextInput"
            :mode="iTextInputModes.TEXTAREA"
            :value="feedbackText"
            placeholder="What was inaccurate about this result?"
            @input="onChangeFeedbackText"
          ></i-text-input>
          <div v-if="validationError" class="i-text-error-90">
            {{ validationError }}
          </div>
        </div>
      </template>
    </i-modal>
  </div>
</template>

<style lang="scss" scoped>
.functionality-disabled {
  pointer-events: none;
}
</style>
